/**
 * The Trump Years — documented controversies, legal findings and abuses across both
 * presidencies (plus the 2021–2025 interval, when most of the first term's legal
 * consequences landed).
 *
 * Editorial rules for this file:
 *  - Every entry states what is documented (court rulings, indictments, official
 *    actions, verified reporting) and links at least one source.
 *  - Allegations are labelled as allegations. Outcomes (acquittals, dismissals,
 *    reversals) are stated alongside the charge.
 *  - Last reviewed: 2026-11-03. The list is not exhaustive.
 */

export type Era = 'first' | 'between' | 'second';

export type Category =
   | 'corruption'
   | 'immigration'
   | 'power'
   | 'justice'
   | 'democracy'
   | 'force'
   | 'press'
   | 'health';

export interface Source {
   label: string;
   url: string;
}

export interface Issue {
   id: string;
   date: string; // ISO yyyy-mm(-dd); month precision is fine
   era: Era;
   category: Category;
   title: string;
   summary: string;
   figure?: string; // a single headline number or fact
   sources: Source[];
}

export const ERAS: Record<Era, { label: string; span: string; start: string; end: string }> = {
   first: { label: 'First term', span: '2017 – 2021', start: '2017-01-20', end: '2021-01-20' },
   between: { label: 'Between terms', span: '2021 – 2025', start: '2021-01-20', end: '2025-01-20' },
   second: { label: 'Second term', span: '2025 – present', start: '2025-01-20', end: '2026-11-03' },
};

export const CATEGORIES: Record<Category, string> = {
   corruption: 'Corruption & self-dealing',
   immigration: 'Immigration & ICE',
   power: 'Abuse of power',
   justice: 'Justice & retribution',
   democracy: 'Elections & democracy',
   force: 'Force & the military',
   press: 'Press freedom',
   health: 'Public health',
};

const wiki = (title: string, label = 'Wikipedia') => ({
   label,
   url: `https://en.wikipedia.org/wiki/${encodeURI(title.replace(/ /g, '_'))}`,
});

export const ISSUES: Issue[] = [
   // ───────────── First term ─────────────
   {
      id: 'mueller',
      date: '2019-04',
      era: 'first',
      category: 'justice',
      title: 'Mueller report details possible obstruction',
      summary:
         'The special counsel found Russia interfered in the 2016 election "in sweeping and systematic fashion" and documented ten episodes of possible obstruction of justice by the president, stating the report "does not exonerate him."',
      figure: '10 episodes of possible obstruction',
      sources: [wiki('Mueller report')],
   },
   {
      id: 'family-separation',
      date: '2018-04',
      era: 'first',
      category: 'immigration',
      title: 'Family separation at the border',
      summary:
         'Under the "zero tolerance" policy, thousands of children were taken from their parents at the U.S.–Mexico border, many without any system to reunite them. Some families were still separated years later.',
      figure: '5,000+ children separated',
      sources: [wiki('Trump administration family separation policy')],
   },
   {
      id: 'foundation',
      date: '2019-11',
      era: 'first',
      category: 'corruption',
      title: 'Trump Foundation shut down for misusing charity funds',
      summary:
         'The foundation was dissolved under court supervision after New York found it was used for political and business purposes, including the 2016 campaign. A judge ordered Trump to pay $2 million in damages.',
      figure: '$2M in damages',
      sources: [wiki('Donald J. Trump Foundation')],
   },
   {
      id: 'hush-money',
      date: '2018-08',
      era: 'first',
      category: 'corruption',
      title: 'Hush money and campaign finance violations',
      summary:
         'Michael Cohen pleaded guilty to campaign finance crimes over 2016 payments to silence Stormy Daniels, made at the candidate\'s direction. The scheme later led to Trump\'s 2024 felony conviction.',
      sources: [wiki('Stormy Daniels–Donald Trump scandal')],
   },
   {
      id: 'ukraine',
      date: '2019-12',
      era: 'first',
      category: 'power',
      title: 'First impeachment: the Ukraine pressure campaign',
      summary:
         'Trump was impeached for abuse of power and obstruction of Congress after withholding military aid while pressing Ukraine\'s president to investigate Joe Biden. The Senate acquitted him in February 2020.',
      figure: 'Impeachment #1',
      sources: [wiki('First impeachment of Donald Trump')],
   },
   {
      id: 'ig-firings-2020',
      date: '2020-05',
      era: 'first',
      category: 'power',
      title: 'Purge of inspectors general',
      summary:
         'Over six weeks, Trump removed or sidelined several independent watchdogs, including the intelligence community IG who forwarded the Ukraine whistleblower complaint and the State Department IG.',
      sources: [wiki('2020 dismissals of inspectors general')],
   },
   {
      id: 'covid',
      date: '2020-03',
      era: 'first',
      category: 'health',
      title: 'Downplaying COVID-19',
      summary:
         'Recorded interviews show Trump knew in early 2020 that the virus was deadly and airborne, while publicly minimizing it. "I wanted to always play it down," he told Bob Woodward.',
      sources: [wiki('Communication of the Trump administration during the COVID-19 pandemic')],
   },
   {
      id: 'lafayette',
      date: '2020-06-01',
      era: 'first',
      category: 'force',
      title: 'Lafayette Square cleared for a photo op',
      summary:
         'Federal officers used chemical irritants, batons and horses to clear largely peaceful protesters near the White House minutes before Trump walked to St. John\'s Church to pose with a Bible.',
      sources: [wiki('Donald Trump photo op at St. John\'s Church')],
   },
   {
      id: 'clemency-allies-2020',
      date: '2020-12',
      era: 'first',
      category: 'justice',
      title: 'Clemency for allies',
      summary:
         'Trump pardoned or commuted the sentences of associates convicted in or linked to the Russia investigation, including Roger Stone, Paul Manafort and Michael Flynn, and later Steve Bannon.',
      sources: [wiki('List of people granted executive clemency in the first Trump presidency')],
   },
   {
      id: 'raffensperger',
      date: '2021-01-02',
      era: 'first',
      category: 'democracy',
      title: '"Find 11,780 votes"',
      summary:
         'In a recorded call, Trump pressed Georgia\'s secretary of state to "find" enough votes to overturn Biden\'s win in the state. The call became central to the Georgia election case.',
      figure: '11,780 votes',
      sources: [wiki('Trump–Raffensperger phone call')],
   },
   {
      id: 'jan6',
      date: '2021-01-06',
      era: 'first',
      category: 'democracy',
      title: 'January 6 and the second impeachment',
      summary:
         'A mob of Trump supporters stormed the Capitol to stop certification of the election. The House impeached Trump for incitement of insurrection; 57 senators voted to convict, short of the two-thirds required.',
      figure: 'Impeachment #2',
      sources: [wiki('January 6 United States Capitol attack'), wiki('Second impeachment of Donald Trump', 'Impeachment')],
   },

   // ───────────── Between terms ─────────────
   {
      id: 'classified-docs',
      date: '2023-06',
      era: 'between',
      category: 'justice',
      title: 'Classified documents kept at Mar-a-Lago',
      summary:
         'After the FBI recovered hundreds of classified documents, Trump was indicted on charges including willful retention of national defense information and obstruction. A judge dismissed the case in 2024, ruling the special counsel\'s appointment invalid.',
      sources: [wiki('Federal prosecution of Donald Trump (classified documents case)')],
   },
   {
      id: 'election-case',
      date: '2023-08',
      era: 'between',
      category: 'democracy',
      title: 'Federal indictment over the 2020 election',
      summary:
         'Trump was charged with conspiring to overturn the 2020 election. The case was dropped after he won in 2024, under Justice Department policy against prosecuting a sitting president.',
      sources: [wiki('Federal prosecution of Donald Trump (election obstruction case)')],
   },
   {
      id: 'carroll',
      date: '2023-05',
      era: 'between',
      category: 'justice',
      title: 'Found liable for sexual abuse of E. Jean Carroll',
      summary:
         'A federal jury found Trump liable for sexually abusing and defaming the writer E. Jean Carroll. A second jury awarded her $83.3 million for further defamation.',
      figure: '$88.3M in damages',
      sources: [wiki('E. Jean Carroll v. Donald J. Trump')],
   },
   {
      id: 'civil-fraud',
      date: '2024-02',
      era: 'between',
      category: 'corruption',
      title: 'Civil fraud: inflated asset values',
      summary:
         'A New York judge found Trump and his company liable for years of fraudulently inflating his net worth to obtain better loans and insurance. An appeals court later voided the roughly $500 million penalty but left the fraud finding in place.',
      sources: [wiki('New York business fraud lawsuit against the Trump Organization')],
   },
   {
      id: 'felony-conviction',
      date: '2024-05-30',
      era: 'between',
      category: 'corruption',
      title: '34 felony convictions',
      summary:
         'A Manhattan jury convicted Trump on 34 counts of falsifying business records to conceal the 2016 hush money payment. He was sentenced to an unconditional discharge in January 2025, days before taking office.',
      figure: '34 felony counts',
      sources: [wiki('Prosecution of Donald Trump in New York')],
   },

   // ───────────── Second term ─────────────
   {
      id: 'memecoin',
      date: '2025-01-17',
      era: 'second',
      category: 'corruption',
      title: 'A presidential meme coin',
      summary:
         'Three days before the inauguration, Trump launched the $TRUMP cryptocurrency, with most of the supply held by affiliated companies. Its largest holders were later invited to a private dinner with the president.',
      sources: [wiki('$Trump')],
   },
   {
      id: 'j6-pardons',
      date: '2025-01-20',
      era: 'second',
      category: 'justice',
      title: 'Mass pardons for January 6 rioters',
      summary:
         'On his first day back in office, Trump granted clemency to roughly 1,500 people charged over the Capitol attack, including people convicted of assaulting police officers and of seditious conspiracy.',
      figure: '~1,500 pardoned',
      sources: [wiki('Pardon of January 6 United States Capitol attack defendants')],
   },
   {
      id: 'ig-firings-2025',
      date: '2025-01-24',
      era: 'second',
      category: 'power',
      title: 'Inspectors general fired en masse',
      summary:
         'About 17 independent inspectors general were dismissed in a single night without the 30-day notice to Congress the law requires.',
      figure: '~17 watchdogs removed',
      sources: [wiki('2025 dismissals of U.S. inspectors general')],
   },
   {
      id: 'doge',
      date: '2025-02',
      era: 'second',
      category: 'power',
      title: 'DOGE and Elon Musk',
      summary:
         'The "Department of Government Efficiency," run by the president\'s largest donor, gutted agencies, accessed sensitive federal data systems and cancelled contracts at agencies that regulate Musk\'s own companies.',
      sources: [wiki('Department of Government Efficiency')],
   },
   {
      id: 'adams',
      date: '2025-02',
      era: 'second',
      category: 'corruption',
      title: 'Dropping the Eric Adams case',
      summary:
         'The Justice Department ordered prosecutors to drop the corruption case against New York\'s mayor. Several prosecutors resigned rather than comply, and the acting U.S. attorney described the move as a quid pro quo for cooperation on immigration.',
      sources: [wiki('Investigations into the Eric Adams administration')],
   },
   {
      id: 'ap-ban',
      date: '2025-02',
      era: 'second',
      category: 'press',
      title: 'The AP barred over "Gulf of America"',
      summary:
         'The White House banned Associated Press journalists from the Oval Office and Air Force One because the AP kept using the name "Gulf of Mexico" in its style guide.',
      sources: [wiki('Associated Press v. Budowich')],
   },
   {
      id: 'cecot',
      date: '2025-03-15',
      era: 'second',
      category: 'immigration',
      title: 'Deportations to a Salvadoran mega-prison',
      summary:
         'Invoking the 1798 Alien Enemies Act, the administration flew more than 200 Venezuelan men to El Salvador\'s CECOT prison without hearings, despite a federal judge\'s order to turn the planes around.',
      figure: '200+ men sent to CECOT',
      sources: [wiki('CECOT')],
   },
   {
      id: 'abrego-garcia',
      date: '2025-03',
      era: 'second',
      category: 'immigration',
      title: 'Kilmar Abrego Garcia',
      summary:
         'A Maryland man with court protection from deportation was sent to CECOT in what the government admitted was an "administrative error." The Supreme Court ordered the administration to facilitate his return.',
      sources: [wiki('Deportation of Kilmar Abrego Garcia')],
   },
   {
      id: 'signal',
      date: '2025-03',
      era: 'second',
      category: 'power',
      title: 'War plans in a group chat',
      summary:
         'Senior officials, including the defense secretary, discussed imminent strike plans in Yemen on Signal, in a chat that had accidentally included the editor of The Atlantic.',
      sources: [wiki('United States government group chat leaks')],
   },
   {
      id: 'law-firms',
      date: '2025-03',
      era: 'second',
      category: 'justice',
      title: 'Executive orders against law firms',
      summary:
         'Trump issued orders punishing law firms tied to his opponents. Courts struck several down as unconstitutional retaliation, while other firms pledged hundreds of millions of dollars in free legal work to avoid being targeted.',
      sources: [wiki('Targeting of law firms and lawyers under the second Trump administration')],
   },
   {
      id: 'qatar-jet',
      date: '2025-05',
      era: 'second',
      category: 'corruption',
      title: 'A $400 million jet from Qatar',
      summary:
         'The administration accepted a luxury Boeing 747-8 from Qatar\'s royal family for use as Air Force One, to be handed to Trump\'s presidential library after he leaves office.',
      figure: '~$400M gift',
      sources: [wiki('Boeing VC-25B Bridge')],
   },
   {
      id: 'la-guard',
      date: '2025-06',
      era: 'second',
      category: 'force',
      title: 'Troops sent to Los Angeles',
      summary:
         'Over the governor\'s objection, Trump federalized the California National Guard and deployed Marines in response to protests against immigration raids. A federal judge later ruled the deployment violated the Posse Comitatus Act.',
      sources: [wiki('June 2025 Los Angeles protests against mass deportation')],
   },
   {
      id: 'alligator-alcatraz',
      date: '2025-07',
      era: 'second',
      category: 'immigration',
      title: '"Alligator Alcatraz"',
      summary:
         'A tent detention camp was built in the Florida Everglades and branded around its alligators. Detainees and lawyers reported flooding, insect infestations and restricted access to counsel.',
      sources: [wiki('Alligator Alcatraz')],
   },
   {
      id: 'paramount',
      date: '2025-07',
      era: 'second',
      category: 'press',
      title: 'Paramount pays, then its merger is approved',
      summary:
         'Paramount paid $16 million to settle Trump\'s lawsuit over a 60 Minutes edit, a suit widely viewed as meritless. Weeks later the FCC approved the company\'s merger with Skydance.',
      figure: '$16M settlement',
      sources: [wiki('Merger of Skydance Media and Paramount Global')],
   },
   {
      id: 'bls',
      date: '2025-08-01',
      era: 'second',
      category: 'power',
      title: 'Statistics chief fired over a jobs report',
      summary:
         'Hours after a weak employment report, Trump fired the commissioner of the Bureau of Labor Statistics, accusing her without evidence of rigging the numbers.',
      sources: [wiki('Erika McEntarfer')],
   },
   {
      id: 'lisa-cook',
      date: '2025-08',
      era: 'second',
      category: 'power',
      title: 'Attempt to fire a Federal Reserve governor',
      summary:
         'Trump moved to remove Fed governor Lisa Cook, the first attempt by a president to fire a sitting governor, as he pressured the central bank to cut interest rates.',
      sources: [wiki('Lisa Cook')],
   },
   {
      id: 'dc-takeover',
      date: '2025-08',
      era: 'second',
      category: 'force',
      title: 'Federal takeover of Washington, D.C.',
      summary:
         'Trump took control of the D.C. police and deployed National Guard troops across the capital, later sending troops to other Democratic-led cities over the objections of local officials.',
      sources: [wiki('Domestic military deployments by the second Trump administration')],
   },
   {
      id: 'boat-strikes',
      date: '2025-09',
      era: 'second',
      category: 'force',
      title: 'Lethal strikes on alleged drug boats',
      summary:
         'The military began destroying boats it said were carrying drugs in the Caribbean and Pacific, killing dozens of people without arrest or trial. Reporting revealed a follow-up strike that killed survivors of an initial attack.',
      sources: [wiki('United States strikes on alleged drug traffickers during Operation Southern Spear')],
   },
   {
      id: 'midway-blitz',
      date: '2025-09',
      era: 'second',
      category: 'immigration',
      title: 'Operation Midway Blitz in Chicago',
      summary:
         'A federal immigration surge brought masked agents, tear gas in residential neighborhoods and the fatal shooting of Silverio Villegas González by an ICE agent during a traffic stop.',
      sources: [wiki('Operation Midway Blitz')],
   },
   {
      id: 'kimmel',
      date: '2025-09',
      era: 'second',
      category: 'press',
      title: 'Jimmy Kimmel taken off the air',
      summary:
         'ABC suspended Jimmy Kimmel Live! after the FCC chairman publicly threatened broadcasters over the host\'s remarks. The show resumed days later after widespread backlash.',
      sources: [wiki('Suspension of Jimmy Kimmel Live!')],
   },
   {
      id: 'homan',
      date: '2025-09',
      era: 'second',
      category: 'corruption',
      title: 'Border czar and the $50,000 bag',
      summary:
         'Reporting revealed that Tom Homan was recorded in September 2024 accepting a bag containing $50,000 in cash from undercover FBI agents posing as business executives. The Justice Department closed the investigation in September 2025, citing insufficient evidence.',
      figure: '$50,000 in cash',
      sources: [wiki('Tom Homan')],
   },
   {
      id: 'comey-james',
      date: '2025-10',
      era: 'second',
      category: 'justice',
      title: 'Prosecuting the president\'s critics',
      summary:
         'After Trump publicly demanded charges, former FBI director James Comey and New York attorney general Letitia James were indicted. Both cases were dismissed in November 2025 because the prosecutor had been unlawfully appointed.',
      sources: [wiki('Prosecution of James Comey'), wiki('Letitia James', 'Letitia James')],
   },
   {
      id: 'cz-pardon',
      date: '2025-10-23',
      era: 'second',
      category: 'corruption',
      title: 'Pardon for the Binance founder',
      summary:
         'Trump pardoned Changpeng Zhao, who had pleaded guilty to money-laundering failures, after Binance helped promote the stablecoin of the Trump family\'s crypto venture, World Liberty Financial.',
      sources: [wiki('Changpeng Zhao'), wiki('World Liberty Financial', 'World Liberty Financial')],
   },
   {
      id: 'santos',
      date: '2025-10',
      era: 'second',
      category: 'justice',
      title: 'George Santos released',
      summary:
         'Trump commuted the 87-month wire fraud and identity theft sentence of the expelled Republican congressman, a few months after he began serving it.',
      sources: [wiki('George Santos')],
   },
   {
      id: 'ballroom',
      date: '2025-10',
      era: 'second',
      category: 'corruption',
      title: 'The East Wing demolished for a donor-funded ballroom',
      summary:
         'The East Wing of the White House was torn down for a ballroom paid for by corporations and donors, many with business before the federal government.',
      sources: [wiki('White House State Ballroom')],
   },
   {
      id: 'epstein',
      date: '2025-11',
      era: 'second',
      category: 'power',
      title: 'The Epstein files',
      summary:
         'After the Justice Department said it would release no further files, Congress passed the Epstein Files Transparency Act over months of resistance from the administration. The release that followed was heavily redacted.',
      sources: [wiki('Epstein Files Transparency Act')],
   },
   {
      id: 'renee-good',
      date: '2026-01-07',
      era: 'second',
      category: 'immigration',
      title: 'Renée Good killed by an ICE agent',
      summary:
         'During Operation Metro Surge in Minneapolis, 37-year-old Renée Good was shot dead by an ICE agent. Federal officials labelled her a "domestic terrorist"; reporting says prosecutors were told to stop investigating.',
      sources: [wiki('Minnesota ICE shootings'), wiki('Operation Metro Surge', 'Operation Metro Surge')],
   },
   {
      id: 'alex-pretti',
      date: '2026-01-24',
      era: 'second',
      category: 'immigration',
      title: 'Alex Pretti killed by federal agents',
      summary:
         'Officials said the 37-year-old ICU nurse was trying to attack agents with a handgun. Verified video showed him holding a cellphone as officers wrestled him to the ground. His death set off the largest protests of the presidency.',
      sources: [wiki('Minnesota ICE shootings')],
   },
   {
      id: 'dhs-shootings',
      date: '2026-02',
      era: 'second',
      category: 'immigration',
      title: 'A pattern of shootings by immigration agents',
      summary:
         'NBC News counted 14 people shot by DHS immigration enforcement officers in the five months from September 2025 to February 2026.',
      figure: '14 people shot in 5 months',
      sources: [
         {
            label: 'NBC News',
            url: 'https://www.nbcnews.com/news/us-news/ice-shootings-list-border-patrol-trump-immigration-operations-rcna254202',
         },
      ],
   },
   {
      id: 'business-pardons',
      date: '2026-07',
      era: 'second',
      category: 'corruption',
      title: 'Pardons that erase corporate penalties',
      summary:
         'By July 2026 Trump had pardoned nine businesses, wiping out about $200 million in penalties, alongside a record wave of white-collar clemency.',
      figure: '~$200M in penalties erased',
      sources: [wiki('Corruption allegations during the second Trump presidency')],
   },
   {
      id: 'family-deals',
      date: '2026-09',
      era: 'second',
      category: 'corruption',
      title: 'The president\'s sons and federal money',
      summary:
         'The New York Times reported $1.6 billion in federal financing for a mining operation in Kazakhstan in which Trump\'s two eldest sons held a 20% stake. In September 2026, Republican senator John Curtis asked the Senate Judiciary Committee to investigate the family\'s business activities.',
      sources: [wiki('Corruption allegations during the second Trump presidency')],
   },
];

/** Headline numbers for the page header. Each ties to an issue above. */
export const HEADLINE_STATS = [
   { value: 2, suffix: '', label: 'Impeachments', ref: 'ukraine' },
   { value: 34, suffix: '', label: 'Felony convictions', ref: 'felony-conviction' },
   { value: 1500, suffix: '', prefix: '~', label: 'Jan 6 defendants pardoned', ref: 'j6-pardons' },
   { value: 5000, suffix: '+', label: 'Children separated at the border', ref: 'family-separation' },
];
